import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  ShieldCheck, Sparkle, Palette, Phone, EnvelopeSimple, MapPin, 
  Factory, House, Car, Package, Warehouse, Hospital, CheckCircle,
  Users, Medal, Certificate
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Customer images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/3gw7xt68_5530d12d-d197-4ee0-960e-bfa644f30c01.jpeg";
const RESIDENTIAL_IMAGE = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/o7errawm_a37b3663-f9e6-4390-9191-da61fe3cbf25.jpeg";
const INDUSTRIAL_IMAGE_LIGHT = "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/7w9bsmm0_1d8f2b94-e132-4cbe-af97-d2a5496e0290.jpeg";
const INDUSTRIAL_IMAGE_DARK = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/blslo1fy_42adb883-6ea5-4455-a1df-6088f45707f8.jpeg";
const GARAGE_IMAGE = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/spfed1d0_42adb883-6ea5-4455-a1df-6088f45707f8.jpeg";
const WORKER_IMAGE = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/wchxewtk_de555155-64a5-48c6-b3a9-17f5ff5056ff.jpeg";
const PU_HARZ_IMAGE = "https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/o1tu5eaz_f4724e5d-d99f-4cbd-adb8-6b47082ba710.jpeg";
const CERTIFICATE_IMAGE = "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/313hg16l_8cc0137c-d91a-4520-a4f9-bdbe9dace718.jpeg";
const CERTIFICATE_IMAGE_2 = "https://customer-assets.emergentagent.com/job_pure-epox-de/artifacts/ek9wyxs1_c6d093ad-eb2c-436f-b0c7-c83f2962b704.jpeg";
const TISCHE_IMAGE = "https://images.unsplash.com/photo-1617638924751-cc232f82ecf9?auto=format&fit=crop&w=1200&q=80";

const LandingPage = () => {
  const navigate = useNavigate();
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Anfrage erfolgreich gesendet!");
        setFormData({ name: "", phone: "", email: "", message: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Benefits data
  const benefits = [
    {
      icon: ShieldCheck,
      title: "Extrem strapazierfähig",
      description: "Unsere Böden halten höchsten Belastungen stand – ideal für Industrie und Gewerbe."
    },
    {
      icon: Sparkle,
      title: "Pflegeleicht & langlebig",
      description: "Einfache Reinigung und jahrelange Haltbarkeit ohne aufwendige Wartung."
    },
    {
      icon: Palette,
      title: "Individuelle Optik möglich",
      description: "Vielfältige Farben und Designs für jeden Geschmack und Anwendungsbereich."
    }
  ];

  // Epoxid data
  const epoxidVorteile = [
    "Extrem hohe Belastbarkeit",
    "Chemikalienbeständig",
    "Fugenlose Oberfläche",
    "Leicht zu reinigen",
    "Lange Lebensdauer",
    "Verschiedene Farben & Designs"
  ];

  const epoxidEinsatz = [
    { icon: Factory, label: "Industriehallen" },
    { icon: Warehouse, label: "Lagerhallen" },
    { icon: Car, label: "Garagen" },
    { icon: Hospital, label: "Kliniken & Labore" },
    { icon: Package, label: "Produktionsstätten" },
    { icon: House, label: "Wohnbereiche" }
  ];

  // PU Harz data
  const puHarzVorteile = [
    "UV-beständig",
    "Elastisch & rissfrei",
    "Temperaturbeständig",
    "Trittsicher",
    "Schnelle Aushärtung",
    "Ideal für Innenbereiche"
  ];

  const puHarzEinsatz = [
    { icon: Factory, label: "Gewerbeflächen" },
    { icon: Warehouse, label: "Keller & Lagerräume" },
    { icon: Car, label: "Garagen" },
    { icon: Hospital, label: "Kühlhäuser" },
    { icon: Package, label: "Lebensmittelindustrie" },
    { icon: House, label: "Wohnräume & Ausstellungsräume" }
  ];

  // Values data
  const values = [
    {
      icon: ShieldCheck,
      title: "Qualität",
      description: "Wir verwenden ausschließlich hochwertige Materialien und setzen auf fachgerechte Verarbeitung."
    },
    {
      icon: Users,
      title: "Kundenorientierung",
      description: "Ihre Zufriedenheit steht für uns an erster Stelle. Wir beraten Sie individuell und transparent."
    },
    {
      icon: MapPin,
      title: "Deutschlandweit",
      description: "Unser Service ist nicht regional begrenzt – wir sind in ganz Deutschland für Sie tätig."
    },
    {
      icon: Medal,
      title: "Zuverlässigkeit",
      description: "Termintreue und professionelle Ausführung sind für uns selbstverständlich."
    }
  ];

  return (
    <div data-testid="landing-page">
      {/* ==================== HERO SECTION ==================== */}
      <section 
        id="hero"
        className="relative min-h-[600px] lg:min-h-[700px] flex items-center"
        data-testid="hero-section"
      >
        <div 
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundPosition: 'center 30%' }}
        >
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6">
              Epoxidharz & PU Harz Böden – langlebig, modern, professionell
            </h1>
            <p className="font-body text-lg sm:text-xl text-slate-200 mb-8 leading-relaxed">
              Robuste und hochwertige Bodenlösungen für Industrie, Gewerbe und Privatkunden – deutschlandweit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection("kontakt")}
                className="btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-6 lg:px-8 py-6 h-auto text-sm lg:text-base w-full sm:w-auto"
                data-testid="hero-cta-primary"
              >
                Jetzt Anfrage stellen
              </Button>
              <Button 
                onClick={() => scrollToSection("leistungen")}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-sm font-semibold uppercase tracking-wide px-6 lg:px-8 py-6 h-auto text-sm lg:text-base w-full sm:w-auto"
                data-testid="hero-cta-secondary"
              >
                Leistungen ansehen
              </Button>
              <Button 
                onClick={() => navigate("/probeflaeche")}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-sm font-semibold uppercase tracking-wide px-6 lg:px-8 py-6 h-auto text-sm lg:text-base w-full sm:w-auto"
                data-testid="hero-cta-probeflaeche"
              >
                Probeflächen ansehen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 bg-white" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-6">
                Moderne Bodenlösungen mit System
              </h2>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Epoxidharz- und PU Harz-Böden bieten eine perfekte Kombination aus Funktionalität und Ästhetik. 
                Sie sind extrem widerstandsfähig gegen mechanische Belastungen, Chemikalien und Abrieb – 
                und dabei optisch ansprechend und vielseitig gestaltbar.
              </p>
              <p className="font-body text-slate-600 leading-relaxed mb-8">
                Ob in der Industriehalle, im Lager, in der Garage oder im modernen Wohnbereich: 
                Unsere Beschichtungen schaffen fugenlose, hygienische und langlebige Oberflächen, 
                die höchsten Ansprüchen gerecht werden.
              </p>
              <Button 
                onClick={() => scrollToSection("kontakt")}
                className="btn-gold text-white rounded-sm font-semibold uppercase tracking-wide px-8 py-6 h-auto"
                data-testid="intro-cta"
              >
                Kostenlose Beratung
              </Button>
            </div>
            <div className="relative">
              <img 
                src={RESIDENTIAL_IMAGE} 
                alt="Moderner Epoxidharzboden im Wohnbereich"
                className="w-full h-[400px] object-cover rounded-sm shadow-lg"
                data-testid="intro-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              Ihre Vorteile auf einen Blick
            </h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">
              Entdecken Sie, warum sich immer mehr Kunden für unsere Harzböden entscheiden.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-sm p-8"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-14 h-14 bg-sky-700 rounded-sm flex items-center justify-center mb-6">
                  <benefit.icon size={28} weight="duotone" className="text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold uppercase tracking-tight text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== LEISTUNGEN SECTION ==================== */}
      <section id="leistungen" className="py-24 bg-white" data-testid="leistungen-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              Unsere Leistungen
            </h2>
            <p className="font-body text-slate-600 text-lg max-w-3xl mx-auto">
              Wir bieten professionelle Bodenbeschichtungen mit Epoxidharz und PU Harz – 
              maßgeschneidert für Ihre Anforderungen in Industrie, Gewerbe und Privatbereich.
            </p>
          </div>

          {/* Epoxidharz Böden */}
          <div className="mb-24" data-testid="epoxid-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <span className="font-body text-sky-700 font-semibold uppercase tracking-wider text-sm">
                  Leistung 01
                </span>
                <h3 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mt-2 mb-6">
                  Epoxidharz Böden
                </h3>
                <p className="font-body text-slate-600 leading-relaxed mb-6">
                  Epoxidharz-Beschichtungen sind die erste Wahl für hochbelastete Bereiche. 
                  Sie bilden eine extrem harte, nahtlose Oberfläche, die mechanischen Belastungen, 
                  Chemikalien und Abrieb standhält. Ideal für Industrie, Gewerbe und moderne Wohnräume.
                </p>

                <div className="mb-6">
                  <h4 className="font-heading text-lg font-semibold uppercase tracking-tight text-slate-900 mb-4">
                    Einsatzbereiche
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {epoxidEinsatz.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2 text-slate-600">
                        <item.icon size={20} weight="duotone" className="text-sky-700" />
                        <span className="font-body text-sm">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="font-heading text-lg font-semibold uppercase tracking-tight text-slate-900 mb-4">
                    Vorteile
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {epoxidVorteile.map((vorteil, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle size={20} weight="fill" className="text-green-600" />
                        <span className="font-body text-slate-600">{vorteil}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => scrollToSection("kontakt")}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-semibold uppercase tracking-wide px-8 py-6 h-auto"
                  data-testid="epoxid-cta"
                >
                  Jetzt anfragen
                </Button>
              </div>
              <div className="space-y-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/6m6cgb7v_4958796c-4d10-47aa-90e1-8fa70ddae517.jpeg" 
                  alt="Epoxidharz Industrieboden"
                  className="w-full h-[280px] object-cover rounded-sm shadow-lg"
                  data-testid="epoxid-image-1"
                />
                <video 
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-[280px] object-cover rounded-sm shadow-lg bg-slate-200"
                  data-testid="epoxid-video-1"
                  poster="https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/6m6cgb7v_4958796c-4d10-47aa-90e1-8fa70ddae517.jpeg"
                >
                  <source src="https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/jx97mw1n_e80ea02c-8955-47e7-bb23-7d9777c5352f.mov" type="video/quicktime" />
                  <source src="https://customer-assets.emergentagent.com/job_9cba13cf-f2db-4d29-a3e0-dbd97dd97a4f/artifacts/jx97mw1n_e80ea02c-8955-47e7-bb23-7d9777c5352f.mov" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>

          {/* PU Harz Böden */}
          <div data-testid="puharz-section">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="order-2 lg:order-1 space-y-4">
                <img 
                  src={PU_HARZ_IMAGE} 
                  alt="PU Harz Boden im Wohnbereich"
                  className="w-full h-[280px] object-cover rounded-sm shadow-lg"
                  data-testid="puharz-image-1"
                />
                <img 
                  src={WORKER_IMAGE} 
                  alt="Professionelle Bodenverlegung"
                  className="w-full h-[280px] object-cover rounded-sm shadow-lg"
                  data-testid="puharz-image-2"
                />
              </div>
              <div className="order-1 lg:order-2">
                <span className="font-body text-sky-700 font-semibold uppercase tracking-wider text-sm">
                  Leistung 02
                </span>
                <h3 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mt-2 mb-6">
                  PU Harz Böden
                </h3>
                <p className="font-body text-slate-600 leading-relaxed mb-6">
                  Polyurethan-Harzbeschichtungen sind besonders flexibel und UV-beständig. 
                  Sie eignen sich hervorragend für Bereiche mit Temperaturwechseln oder UV-Belastung 
                  und bieten eine langlebige, elastische Oberfläche ohne Rissbildung.
                </p>

                <div className="mb-6">
                  <h4 className="font-heading text-lg font-semibold uppercase tracking-tight text-slate-900 mb-4">
                    Einsatzbereiche
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {puHarzEinsatz.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2 text-slate-600">
                        <item.icon size={20} weight="duotone" className="text-sky-700" />
                        <span className="font-body text-sm">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="font-heading text-lg font-semibold uppercase tracking-tight text-slate-900 mb-4">
                    Vorteile
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {puHarzVorteile.map((vorteil, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle size={20} weight="fill" className="text-green-600" />
                        <span className="font-body text-slate-600">{vorteil}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => scrollToSection("kontakt")}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-semibold uppercase tracking-wide px-8 py-6 h-auto"
                  data-testid="puharz-cta"
                >
                  Jetzt anfragen
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== ÜBER UNS SECTION ==================== */}
      <section id="ueber-uns" className="py-24 bg-slate-50" data-testid="ueber-uns-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              Über Uns
            </h2>
            <p className="font-body text-slate-600 text-lg max-w-2xl mx-auto">
              Lernen Sie Pure Epox kennen – Ihr Partner für hochwertige Harzböden in ganz Deutschland.
            </p>
          </div>

          {/* Zentrierter Text ohne Bild */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h3 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-8">
              Pure Epox – Qualität & Zuverlässigkeit
            </h3>
            <p className="font-body text-slate-600 text-lg leading-relaxed mb-6">
              Pure Epox steht für hochwertige Harzböden mit Fokus auf Qualität und Zuverlässigkeit. 
              Wir arbeiten deutschlandweit und bieten individuelle Lösungen für jedes Projekt.
            </p>
            <p className="font-body text-slate-600 text-lg leading-relaxed mb-6">
              Unser Team verfügt über langjährige Erfahrung in der Verarbeitung von Epoxidharz- 
              und PU Harz-Beschichtungen. Wir wissen, worauf es ankommt, und setzen dieses Wissen 
              konsequent für unsere Kunden ein.
            </p>
            <p className="font-body text-slate-600 text-lg leading-relaxed">
              Ob Industriehalle, Gewerbefläche oder privater Wohnbereich – wir beraten Sie 
              umfassend und finden gemeinsam die optimale Lösung für Ihre Anforderungen.
            </p>
          </div>

          {/* Values */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-100 shadow-sm rounded-sm p-8 text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-sky-700 rounded-sm flex items-center justify-center mx-auto mb-6">
                  <value.icon size={32} weight="duotone" className="text-white" />
                </div>
                <h4 className="font-heading text-xl font-semibold uppercase tracking-tight text-slate-900 mb-3">
                  {value.title}
                </h4>
                <p className="font-body text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ZERTIFIZIERTE QUALITÄT SECTION ==================== */}
      {CERTIFICATE_IMAGE && (
        <section className="py-24 bg-white" data-testid="zertifikat-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 mb-4">
                Zertifizierte Qualität
              </h2>
              <p className="font-body text-slate-600 text-lg max-w-2xl mx-auto">
                Unsere Arbeit steht für Qualität und Professionalität. Durch unsere Zertifizierung 
                garantieren wir fachgerechte Verarbeitung und hochwertige Ergebnisse.
              </p>
            </div>

            <div className="flex flex-col items-center gap-8">
              {/* First Certificate */}
              <div className="bg-slate-50 border-8 border-slate-200 rounded-sm p-4 sm:p-8 shadow-lg max-w-2xl">
                <img 
                  src={CERTIFICATE_IMAGE} 
                  alt="Pure Epox Zertifikat"
                  className="w-full h-auto object-contain"
                  data-testid="certificate-image"
                />
              </div>

              {/* Second Certificate */}
              {CERTIFICATE_IMAGE_2 && (
                <div className="bg-slate-50 border-8 border-slate-200 rounded-sm p-4 sm:p-8 shadow-lg max-w-2xl">
                  <img 
                    src={CERTIFICATE_IMAGE_2} 
                    alt="Pure Epox Zertifikat 2"
                    className="w-full h-auto object-contain"
                    data-testid="certificate-image-2"
                  />
                </div>
              )}
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex items-center justify-center bg-sky-700 text-white px-6 py-3 rounded-sm">
                <Certificate size={24} weight="duotone" className="mr-3" />
                <span className="font-body font-semibold">Geprüfte Fachkompetenz</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ==================== KONTAKT SECTION ==================== */}
      <section id="kontakt" className="py-24 bg-white" data-testid="kontakt-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 mb-4">
              Kontakt
            </h2>
            <p className="font-body text-slate-600 text-lg max-w-2xl mx-auto">
              Haben Sie Fragen oder möchten Sie ein Angebot? 
              Wir freuen uns auf Ihre Nachricht und beraten Sie gerne.
            </p>
          </div>

          {/* Ansprechpartner - Zwei Karten */}
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900 mb-8 text-center">
              Ihre Ansprechpartner
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Sinan Kaya */}
              <div className="bg-slate-50 rounded-sm p-6 border border-slate-200" data-testid="contact-card-sinan">
                <p className="font-heading text-2xl font-bold text-slate-900 mb-1">
                  Sinan Kaya
                </p>
                <p className="font-body text-sm text-slate-500 mb-2">
                  Projektleitung & fachliche Kundenberatung
                </p>
                <a 
                  href="mailto:pureepox@gmail.com" 
                  className="font-body text-slate-600 hover:text-sky-700 text-sm mb-6 block"
                >
                  pureepox@gmail.com
                </a>
                
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-sky-700 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone size={18} weight="bold" className="text-white" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-slate-500 uppercase tracking-wider">Telefon</p>
                    <a 
                      href="tel:+4916092653278" 
                      className="font-body text-sky-700 hover:text-sky-800 font-medium"
                      data-testid="kontakt-sinan-phone"
                    >
                      +49 160 92653278
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Semih Aytekin */}
              <div className="bg-slate-50 rounded-sm p-6 border border-slate-200" data-testid="contact-card-semih">
                <p className="font-heading text-2xl font-bold text-slate-900 mb-1">
                  Semih Aytekin
                </p>
                <p className="font-body text-sm text-slate-500 mb-2">
                  Projektplanung, Organisation & Kundenmanagement
                </p>
                <a 
                  href="mailto:pureepox@gmail.com" 
                  className="font-body text-slate-600 hover:text-sky-700 text-sm mb-6 block"
                >
                  pureepox@gmail.com
                </a>
                
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-500 rounded-sm flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-body text-xs text-slate-500 uppercase tracking-wider">WhatsApp</p>
                    <a 
                      href="https://wa.me/4917672636169?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zu%20Ihren%20Leistungen."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-green-600 hover:text-green-700 font-medium"
                      data-testid="kontakt-semih-whatsapp"
                    >
                      Nachricht senden
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Zusätzliche Infos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Adresse */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-sky-700 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h4 className="font-heading text-sm font-semibold uppercase tracking-tight text-slate-900 mb-1">
                    Adresse
                  </h4>
                  <p className="font-body text-slate-600 text-sm leading-relaxed">
                    Steubenstraße 23<br />
                    35576 Wetzlar
                  </p>
                </div>
              </div>
              
              {/* E-Mail */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-sky-700 rounded-sm flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h4 className="font-heading text-sm font-semibold uppercase tracking-tight text-slate-900 mb-1">
                    E-Mail
                  </h4>
                  <a 
                    href="mailto:pureepox@gmail.com" 
                    className="font-body text-sky-700 hover:text-sky-800 text-sm"
                    data-testid="kontakt-email-link"
                  >
                    pureepox@gmail.com
                  </a>
                </div>
              </div>
              
              {/* Servicegebiet */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-sky-700 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h4 className="font-heading text-sm font-semibold uppercase tracking-tight text-slate-900 mb-1">
                    Servicegebiet
                  </h4>
                  <p className="font-body text-slate-600 text-sm">
                    Deutschlandweit
                  </p>
                </div>
              </div>
            </div>
            
            {/* Hinweis & USt-ID */}
            <div className="text-center pt-6 border-t border-slate-200">
              <p className="font-body text-sm text-slate-500 mb-2">
                E-Mail-Anfragen werden zentral durch unser Team bearbeitet. Besichtigungstermine nach Vereinbarung.
              </p>
              <p className="font-body text-xs text-slate-400">
                USt-IdNr.: DE458589866
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
            Bereit für Ihren neuen Boden?
          </h2>
          <p className="font-body text-slate-300 max-w-2xl mx-auto mb-8 text-lg">
            Kontaktieren Sie uns für eine unverbindliche Beratung. Wir erstellen Ihnen ein individuelles Angebot – deutschlandweit.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+4916092653278">
              <Button 
                className="btn-gold text-white rounded-sm font-bold uppercase tracking-wide px-8 py-6 h-auto text-base w-full"
                data-testid="cta-phone-button"
              >
                <Phone size={20} weight="bold" className="mr-2" />
                Jetzt anrufen
              </Button>
            </a>
            <a 
              href="https://wa.me/4917672636169?text=Hallo%2C%20ich%20interessiere%20mich%20für%20Ihre%20Epoxidharz-Böden."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-green-500 hover:bg-green-600 text-white rounded-sm font-bold uppercase tracking-wide px-8 py-6 h-auto text-base w-full"
                data-testid="cta-whatsapp-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Per WhatsApp
              </Button>
            </a>
            <a href="mailto:pureepox@gmail.com?subject=Anfrage%20über%20Pure%20Epox%20Website">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-sm font-semibold uppercase tracking-wide px-8 py-6 h-auto text-base w-full"
                data-testid="cta-email-button"
              >
                <EnvelopeSimple size={20} weight="bold" className="mr-2" />
                E-Mail senden
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
